import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { useQueryConfigurationBusinessTrips } from '@/lib/api/company/configuration/businessTrips/useQueryConfigurationBusinessTrips'
import { getCountryFlag } from '@/modules/shared/config/country-flag'

import React from 'react'

const MANY_DESTINATIONS_NATION_ID = 0
const EUROPE_NATION_ID = 500

const AddCountryCodeCountry = ({ data, setData, companyId }) => {
  const { t } = useLocalize()
  const {
    data: allCountries = [],
    isPending,
    isError,
  } = useQueryCountries({
    companyId: companyId,
  })
  const countries = React.useMemo(
    () =>
      allCountries.filter(
        (country) =>
          country.nationId !== MANY_DESTINATIONS_NATION_ID &&
          country.nationId !== EUROPE_NATION_ID,
      ),
    [allCountries],
  )

  const { data: configuration } = useQueryConfigurationBusinessTrips(companyId)
  const configuredCodes =
    configuration?.priorityCountries
      ?.map((country) => country.code?.toUpperCase())
      .filter(Boolean) ?? []
  const priorityCodes = configuredCodes.length
    ? configuredCodes
    : ['PL', 'DE', 'FR', 'AT', 'BE', 'CZ', 'UA', 'RU']
  const quickCountries = [...new Set(priorityCodes)]
    .map((code) =>
      countries.find((country) => country.code.toUpperCase() === code),
    )
    .filter(Boolean)

  React.useEffect(() => {
    if (!data.nationality) {
      const poland = countries.find(
        (country) => country.code.toUpperCase() === 'PL',
      )
      if (poland) setData((current) => ({ ...current, nationality: poland }))
    }
  }, [countries, data.nationality, setData])
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <label
        htmlFor="countryCode-country"
        className="tw-text-sm tw-font-semibold tw-text-content-base"
      >
        {t('countryCode.country')}
      </label>
      <select
        id="countryCode-country"
        name="countryCode-country"
        required
        disabled={isPending || isError || countries.length === 0}
        className="tw-w-full tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-text-content-base"
        value={data?.nationality?.id?.toString() ?? ''}
        onChange={(e) => {
          const id = e.target.value

          const countryObj = Array.isArray(countries)
            ? countries.find((c) => c.id.toString() === id)
            : null

          setData({
            ...data,
            nationality: countryObj ?? null,
          })
        }}
      >
        <option value="">{t('common.select')}</option>
        {countries.map((country) => (
          <option key={country.id} value={country.id}>
            {country.name}
          </option>
        ))}
      </select>
      {isPending && <p role="status">{t('countryCode.loading-countries')}</p>}
      {isError && (
        <p role="alert" className="tw-text-error">
          {t('countryCode.countries-error')}
        </p>
      )}
      {!isPending && !isError && countries.length === 0 && (
        <p role="status">{t('countryCode.no-countries')}</p>
      )}
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {quickCountries.map((country) => (
          <button
            key={country.id}
            type="button"
            title={country.name}
            aria-label={country.name}
            aria-pressed={data.nationality?.id === country.id}
            onClick={() => setData({ ...data, nationality: country })}
            className={`tw-flex tw-min-w-10 tw-flex-col tw-items-center tw-gap-1 tw-rounded tw-border tw-p-2 tw-text-xs ${data.nationality?.id === country.id ? 'tw-border-primary tw-bg-base-300' : 'tw-border-base-300'}`}
          >
            <img
              src={getCountryFlag(country.code)}
              alt=""
              className="tw-h-4 tw-w-6 tw-object-contain"
            />
            {country.code}
          </button>
        ))}
      </div>
    </div>
  )
}

export default AddCountryCodeCountry
